import { useState, useEffect, useRef } from 'react';
import "react-datepicker/dist/react-datepicker.css";
import CalendarInput from '../shared/CalendarInput';
import { toHtmlInputDate, fromHtmlInputDate } from '../../utils/dateUtils';
import { LOAN_PROGRAM_CODES } from '../../config/organizations';
import {
    isLoanProgram,
    resolveFieldOptions,
    toEncompassLoanProgram
} from '../../utils/loanProgramUtil';

const AdditionalInformation = ({
    additionalFields,
    fieldValues,
    bpcPrograms,
    hiddenFieldIds = [],
    onFieldChange,
    isLoanSubmitted = false
}) => {
    const [localFieldValues, setLocalFieldValues] = useState({});
    const fieldsJson = additionalFields;
    const [disable389, setDisable389] = useState(false);
    const loadedDateMinsRef = useRef({});
    const hiddenFieldSet = new Set(hiddenFieldIds || []);

    useEffect(() => {
        setLocalFieldValues(prev => {
            const next = { ...prev };

            for (const field of additionalFields) {
                // Only overwrite if parent actually provides a value for this field
                if (!(fieldValues && Object.prototype.hasOwnProperty.call(fieldValues, field.id))) {
                    continue;
                }

                let val = fieldValues?.[field.id];

                // Encompass field 420 returns display values such as "Second Lien".
                // Convert them back to the contract values used by the dropdown.
                if (field.id === "420") {
                    const lienPosition = (val ?? "").toString().replace(/\s+/g, "").toLowerCase();

                    if (lienPosition === "firstlien" || lienPosition === "first") {
                        val = "FirstLien";
                    } else if (lienPosition === "secondlien" || lienPosition === "subordinate") {
                        val = "SecondLien";
                    }
                }

                // normalization you already had
                if (field.datatype === "date" && val === "//") {
                    val = "";
                }
                if (field.id === "4459") {
                    val = "N";
                }
                if (field.format === "YN_BOOL") {
                    if (val === "Y") val = "true";
                    else if (val === "N") val = "false";
                }

                next[field.id] = val ?? "";

                // capture "loaded baseline" date for minFromLoaded validation
                if (field.datatype === "date" && field.validation?.minFromLoaded) {
                    const vStr = (val ?? "").toString().trim();
                    if (vStr && vStr !== "//") {

                        // don't let unsaved edits shift the baseline
                        const isDirty = localStorage.getItem("loan_additional_info_dirty") === "true";
                        if (!isDirty) {
                            loadedDateMinsRef.current[field.id] = vStr;
                        }
                    }
                }
            }

            return next;
        });
    }, [fieldValues, additionalFields]);

    const handleChange = (e, field) => {
        const { value } = e.target;

        // update local state so the UI updates instantly
        setLocalFieldValues(prev => ({
            ...prev,
            [field.id]: value
        }));

        // prepare value to send to parent (reverse any display-only transform)
        let valueToSend = value;

        // If field uses YN_BOOL we displayed "true"/"false" — send Y/N upstream
        if (field.format === "YN_BOOL") {
            if (value === "true") valueToSend = "Y";
            else if (value === "false") valueToSend = "N";
            else valueToSend = value; // fallback
        }

        // call parent's handler if provided
        if (onFieldChange) onFieldChange(field.id, valueToSend);
    };

    const handleIntegerInput = (rawValue, field) => {
        // allow only digits
        const newValue = rawValue.replace(/[^\d]/g, '');
        handleChange({ target: { value: newValue } }, field);
    };

    const getDateValidation = (fieldId) => {
        const def = fieldsJson.find(f => f.id === fieldId);
        return def?.validation || {};
    };

    const getFieldDef = (id) => additionalFields.find(f => f.id === id);

    const conditionMatches = (condition, values) => {
        const currentValue = (values?.[condition.field] ?? "").toString().trim();

        if (condition.equals != null) {
            return currentValue === String(condition.equals).trim();
        }

        if (Array.isArray(condition.in)) {
            return condition.in.map(String).includes(currentValue);
        }

        return false;
    };

    const ruleMatches = (ruleIf, values) => {
        if (!ruleIf) return false;

        if (Array.isArray(ruleIf.all)) {
            return ruleIf.all.every(condition => conditionMatches(condition, values));
        }

        return conditionMatches(ruleIf, values);
    };

    const getEffectiveReadonly = (fieldDef, values) => {
        const rules = Array.isArray(fieldDef?.optionRules) ? fieldDef.optionRules : [];
        let readonly = fieldDef?.readonly;

        for (const rule of rules) {
            if (rule?.if && ruleMatches(rule.if, values) && typeof rule?.then?.readonly === "boolean") {
                readonly = rule.then.readonly;
            }
        }

        return readonly;
    };

    const getRuleSetValue = (fieldDef, values) => {
        const rules = Array.isArray(fieldDef?.optionRules) ? fieldDef.optionRules : [];

        for (const rule of rules) {
            if (rule?.if && ruleMatches(rule.if, values) && rule?.then?.setValue != null) {
                return rule.then.setValue;
            }
        }

        return null;
    };

    const normalizeOptions = (opts = []) =>
        (opts || []).map(o => typeof o === 'string' ? { value: o, text: o } : { value: String(o.value), text: o.text || o.value });
    const getAllowedIncomeOptionValues = (doc) => {
        const dep = getFieldDef('CX.DOC.TYPE')?.dependentOptions;
        return (dep && doc) ? (dep.map?.[doc] || []).map(String) : [];
    };

    const incomeFieldDef = getFieldDef('CX.INCOME.TYPE');
    const allIncomeOptions = normalizeOptions(incomeFieldDef?.options || []);
    const selectedDocValue = localFieldValues['CX.DOC.TYPE'] || '';
    const allowedIncomeValues = getAllowedIncomeOptionValues(selectedDocValue);
    const filteredIncomeOptions = selectedDocValue === '' ? [] :
        (allowedIncomeValues.length === 0 ? allIncomeOptions : allIncomeOptions.filter(o => allowedIncomeValues.includes(o.value)));

    const initialDocSyncRef = useRef(true);

    useEffect(() => {
        const docVal = localFieldValues['CX.DOC.TYPE'] || '';
        const allowed = getAllowedIncomeOptionValues(docVal) || [];
        const currentIncome = localFieldValues['CX.INCOME.TYPE'] || '';
        const currentDSCRLoan = localFieldValues['CX.DSCR.LOAN.ESTIMATED'] || '';

        const setFieldValue = (id, val) => {
            setLocalFieldValues(p => ({ ...p, [id]: val }));
            if (onFieldChange) onFieldChange(id, val);
        };

        // Special rule: if DSCR is selected, default income type to DSCR and DSCR dropdown to Yes
        if (docVal === 'DSCR') {
            if (currentIncome !== 'DSCR') setFieldValue('CX.INCOME.TYPE', 'DSCR');
            if (currentDSCRLoan !== 'Y') setFieldValue('CX.DSCR.LOAN.ESTIMATED', 'Y');
            return;
        }
        else {
            setFieldValue('CX.DSCR.ESTIMATED', '');
        }

        // Clear DSCR fields when switching away
        if (currentDSCRLoan === 'Y' && docVal !== 'DSCR') {
            setFieldValue('CX.DSCR.LOAN.ESTIMATED', 'N');
        }

        if (initialDocSyncRef.current) {
            initialDocSyncRef.current = false;
            if (!docVal) {
                if (currentIncome) setFieldValue('CX.INCOME.TYPE', '');
                return;
            }
            if (currentIncome) {
                if (!allowed.includes(currentIncome)) setFieldValue('CX.INCOME.TYPE', allowed[0] || '');
                return;
            }
            if (!currentIncome && allowed.length > 0) setFieldValue('CX.INCOME.TYPE', allowed[0] || '');
            return;
        }

        if (!docVal) {
            if (currentIncome) setFieldValue('CX.INCOME.TYPE', '');
            return;
        }

        if (allowed.length > 0 && (!currentIncome || !allowed.includes(currentIncome))) {
            setFieldValue('CX.INCOME.TYPE', '');
        }
    }, [localFieldValues['CX.DOC.TYPE']]);



    // normalize value for rule comparison (coarse)
    const normalizeForRule = (v) => {
        if (v === null || v === undefined) return "";
        const s = String(v).toUpperCase().trim();
        if (s === "Y" || s === "YES" || s === "TRUE") return "YES";
        if (s === "N" || s === "NO" || s === "FALSE") return "NO";
        return s;
    };

    // Is the given field id conditionally required by any rule on other fields?
    const isConditionallyRequired = (fieldId) => {
        if (!Array.isArray(additionalFields)) return false;

        // find rules in other fields where then.field === fieldId and evaluate if their "if" triggers currently
        for (const f of additionalFields) {
            if (!Array.isArray(f.rules)) continue;

            for (const r of f.rules) {
                const thenField = r?.then?.field;
                const triggerEquals = r?.if?.equals;
                if (!thenField || thenField !== fieldId || triggerEquals == null) continue;

                const current = localFieldValues[f.id];
                if (normalizeForRule(current) === normalizeForRule(triggerEquals)) {
                    // this rule is active and requires this field
                    if (r.then.required === true) return true;
                }
            }
        }
        return false;
    };

    const removeHighlightForField = (fieldId) => {
        const el = document.getElementById(`field-${fieldId}`);
        if (!el) return;
        if (el.hasAttribute('data-required-missing-input')) {
            el.removeAttribute('data-required-missing-input');
            el.style.border = '';
        }
    };

    useEffect(() => {
        const product = localFieldValues['CX.LOAN.PROD.BROKER.SELECT'];

        // Allowed Documentation Type options per product, keyed by canonical code.
        const productDocOptions = {
            [LOAN_PROGRAM_CODES.PROGRAM_002]: ["Full Doc", "Alt Doc", "DSCR", "None"], // ACCELERATOR / BREEZY
            [LOAN_PROGRAM_CODES.PROGRAM_005]: ["Full Doc", "Alt Doc", "DSCR", "None"], // ACCELERATOR ITIN / BREEZY ITIN
            [LOAN_PROGRAM_CODES.PROGRAM_007]: ["Full Doc", "Alt Doc", "DSCR", "None"], // AMBASSADOR / PASSPORT
            [LOAN_PROGRAM_CODES.PROGRAM_008]: ["Full Doc", "Alt Doc", "None"],         // SUPER JUMBO / STARRY
            [LOAN_PROGRAM_CODES.PROGRAM_003]: ["Full Doc", "Alt Doc", "None"],         // ACCELERATOR INCOME / BREEZY INCOME
            [LOAN_PROGRAM_CODES.PROGRAM_010]: ["DSCR"]                                 // ACCELERATOR EQUITY FIRST / CLOUDY
        };

        const allowedDocs = productDocOptions[product] || [
            "Full Doc",
            "Alt Doc",
            "DSCR",
            "None"
        ];

        // Update field definition dynamically (filtered options)
        const docField = getFieldDef('CX.DOC.TYPE');
        if (docField) {
            docField.filteredOptions = allowedDocs;
        }

        const currentDoc = localFieldValues['CX.DOC.TYPE'];

        // If Accelerator Equity First / Cloudy, auto-select DSCR
        if (isLoanProgram(product, LOAN_PROGRAM_CODES.PROGRAM_010) && currentDoc !== 'DSCR') {
            setLocalFieldValues(prev => ({
                ...prev,
                'CX.DOC.TYPE': 'DSCR'
            }));

            if (onFieldChange) {
                onFieldChange('CX.DOC.TYPE', 'DSCR');
            }

            return;
        }

        // If current doc type is invalid for selected product, clear it
        if (currentDoc && !allowedDocs.includes(currentDoc)) {
            setLocalFieldValues(prev => ({
                ...prev,
                'CX.DOC.TYPE': ''
            }));
            if (onFieldChange) onFieldChange('CX.DOC.TYPE', '');
        }

    }, [localFieldValues['CX.LOAN.PROD.BROKER.SELECT']]);

    useEffect(() => {
        (additionalFields || []).forEach(field => {
            const forcedValue = getRuleSetValue(field, localFieldValues);
            if (forcedValue == null) return;

            const currentValue = (localFieldValues[field.id] ?? "").toString();
            if (currentValue === forcedValue) return;

            setLocalFieldValues(prev => ({ ...prev, [field.id]: forcedValue }));
            if (onFieldChange) onFieldChange(field.id, forcedValue);
        });
    }, [localFieldValues["CX.LOAN.PROD.BROKER.SELECT"]]);


    // ======================================================
    // Auto-fill & lock field 389 when conditions are met
    // ======================================================
    useEffect(() => {
        if (!bpcPrograms || bpcPrograms.length === 0) return;

        const loanProd = localFieldValues["CX.LOAN.PROD.BROKER.SELECT"];

        const matchRequiredProd = isLoanProgram(loanProd, [
            LOAN_PROGRAM_CODES.PROGRAM_002, // ACCELERATOR / BREEZY
            LOAN_PROGRAM_CODES.PROGRAM_005, // ACCELERATOR ITIN / BREEZY ITIN
            LOAN_PROGRAM_CODES.PROGRAM_007, // AMBASSADOR / PASSPORT
            LOAN_PROGRAM_CODES.PROGRAM_003, // ACCELERATOR INCOME / BREEZY INCOME
            LOAN_PROGRAM_CODES.PROGRAM_008  // SUPER JUMBO / STARRY
        ]);

        if (matchRequiredProd) {
            // BPC programs come from the pricing API keyed by the Encompass
            // product name, not by canonical code - translate before matching.
            const encompassProductName = toEncompassLoanProgram(loanProd);
            const program = bpcPrograms.find(p => p.ProgramName === encompassProductName);

            if (program) {
                // Set 389 automatically
                const newVal = program.BPC_Percentage;

                setLocalFieldValues(prev => ({
                    ...prev,
                    "389": newVal
                }));

                if (onFieldChange) {
                    onFieldChange("389", newVal);
                }

                setDisable389(true);
            }
            else {
                setDisable389(false);
            }
        } else {
            // ==========================
            // CLEAR 389 when rule fails
            // ==========================
            setLocalFieldValues(prev => ({
                ...prev,
                "389": ""
            }));

            if (onFieldChange) {
                onFieldChange("389", "");
            }

            setDisable389(false);
        }
    }, [localFieldValues["CX.LOAN.PROD.BROKER.SELECT"], bpcPrograms]);


    return (
        <div className="border border-[var(--blue-2)] rounded bg-[#f9f9f9] mb-3">
            <div className="section-header">Additional Information</div>
            <div className="grid md:grid-cols-4 gap-6 mt-4 px-4 py-4">
                {additionalFields
                    .filter(field => !hiddenFieldSet.has(field.id))
                    .map((baseField) => {
                        const field =
                            isLoanSubmitted && baseField.id === "1548"
                                ? {
                                    ...baseField,
                                    control: "textbox",
                                    readonly: true
                                }
                                : baseField;

                        const value = localFieldValues[field.id] || "";
                        const effectiveReadonly = getEffectiveReadonly(field, localFieldValues);
                        const validation = getDateValidation(field.id);
                        const todayISO = new Date().toISOString().substring(0, 10);

                        // compute dynamic required mark
                        const dynamicRequired = isConditionallyRequired(field.id);
                        const showAsterisk = field.required || dynamicRequired;

                        let minDate;

                        // existing rule
                        if (validation.noPast) {
                            minDate = todayISO;
                        }

                        // minFromLoaded (Start Date rule)
                        if (field.validation?.minFromLoaded) {
                            const baseline = loadedDateMinsRef.current[field.id];
                            if (baseline) {
                                const baseISO = new Date(baseline).toISOString().slice(0, 10);
                                minDate = minDate
                                    ? (minDate > baseISO ? minDate : baseISO)
                                    : baseISO;
                            }
                        }


                        return (
                            <div key={field.id}>
                                <label className="form-label">
                                    {field.name}
                                    {showAsterisk && <span className="text-red-600 ml-1">*</span>}
                                </label>

                                {field.control === "textbox" && field.datatype === "date" && (
                                    <CalendarInput
                                        value={toHtmlInputDate(localFieldValues[field.id])}
                                        id={`field-${field.id}`}
                                        onChange={(e) => {
                                            const formatted = fromHtmlInputDate(e.target.value);

                                            removeHighlightForField(field.id);

                                            setLocalFieldValues(prev => ({
                                                ...prev,
                                                [field.id]: formatted
                                            }));
                                            if (onFieldChange) onFieldChange(field.id, formatted);
                                        }}
                                        min={minDate}
                                        max={validation.noFuture ? todayISO : undefined}
                                        readOnly={effectiveReadonly}
                                    />
                                )}

                                {field.control === "textbox" && field.datatype !== "date" && (
                                    <input
                                        type="text"
                                        className="form-input"
                                        id={`field-${field.id}`}
                                        value={value}
                                        onChange={(e) => {
                                            let newValue = e.target.value;

                                            removeHighlightForField(field.id);

                                            if (field.datatype === "integer") {
                                                handleIntegerInput(newValue, field);
                                                return;
                                            }

                                            if (field.datatype === "decimal") {
                                                if (/^\d*\.?\d*$/.test(newValue)) {
                                                    handleChange({ target: { value: newValue } }, field);
                                                }
                                            } else {
                                                handleChange(e, field);
                                            }
                                        }}

                                        readOnly={
                                            effectiveReadonly ||
                                            (field.id === '389' && disable389)
                                        }
                                    />
                                )}

                                {field.id === 'CX.INCOME.TYPE' && (
                                    <select
                                        className="form-input w-full text-sm border rounded p-1"
                                        id={`field-${field.id}`}
                                        value={localFieldValues[field.id] || ""}
                                        disabled={effectiveReadonly}
                                        onChange={(e) => {
                                            const newVal = e.target.value;

                                            removeHighlightForField(field.id);
                                            setLocalFieldValues(prev => ({ ...prev, [field.id]: newVal }));
                                            if (onFieldChange) onFieldChange(field.id, newVal);
                                        }}
                                    >
                                        <option value="">Select an Option</option>

                                        {(() => {
                                            const product = (localFieldValues["CX.LOAN.PROD.BROKER.SELECT"] || "").toString().trim().toUpperCase();
                                            const currentIncome = (localFieldValues["CX.INCOME.TYPE"] || "").toString().trim();

                                            const OLD_STD = "Bank Statement - Standard";
                                            const OLD_STR = "Bank Statement - Streamline";
                                            const NEW_12 = "Bank Statement - Standard 12 months";
                                            const NEW_24 = "Bank Statement - Standard 24 months";

                                            // Start from your existing list (already in config order)
                                            let opts = (filteredIncomeOptions || []).slice();

                                            if (isLoanProgram(product, LOAN_PROGRAM_CODES.PROGRAM_009)) {
                                                // Activator Prime / Sunny Select: show ONLY new 12/24, keep original order
                                                opts = opts.filter(o => o?.value === NEW_12 || o?.value === NEW_24 || o?.value === OLD_STR);
                                            } else {
                                                // Not Activator Prime / Sunny Select:
                                                // - always keep new 12/24
                                                // - keep ONLY the legacy value if the loan currently uses it
                                                // - otherwise hide both legacy values
                                                opts = opts.filter(o => {
                                                    const v = o?.value;

                                                    if (v === OLD_STD) return currentIncome === OLD_STD;       // show only if current is Standard
                                                    //if (v === OLD_STR) return currentIncome === OLD_STR;       // show only if current is Streamline
                                                    return true;                                               // keep everything else in order
                                                });
                                            }

                                            return opts.map(opt => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.text}
                                                </option>
                                            ));
                                        })()}
                                    </select>
                                )}
                                {field.control === "dropdown" && field.id !== 'CX.INCOME.TYPE' && field.id !== 'CX.DSCR.ESTIMATED' && (
                                    <select
                                        className="form-input w-full text-sm border rounded p-1"
                                        id={`field-${field.id}`}
                                        value={localFieldValues[field.id] || ""}
                                        disabled={
                                            effectiveReadonly ||
                                            (field.id === 'CX.DSCR.LOAN.ESTIMATED' && localFieldValues['CX.DOC.TYPE'] !== 'DSCR')
                                        }
                                        onChange={(e) => {
                                            const newVal = e.target.value;

                                            removeHighlightForField(field.id);

                                            setLocalFieldValues((prev) => ({
                                                ...prev,
                                                [field.id]: newVal
                                            }));

                                            let valueToSend = newVal;
                                            if (field.format === "YN_BOOL") {
                                                if (newVal === "true") valueToSend = "Y";
                                                else if (newVal === "false") valueToSend = "N";
                                            }

                                            if (onFieldChange) onFieldChange(field.id, valueToSend);

                                            if (field.id === "CX.LOAN.PROD.BROKER.SELECT" && onFieldChange) {
                                                onFieldChange(field.id, valueToSend);
                                            }
                                        }}

                                    >
                                        <option value="">Select an Option</option>

                                        {field.id === 'CX.DOC.TYPE'
                                            ? (field.filteredOptions || field.options).map(opt => (
                                                <option key={opt} value={opt}>{opt}</option>
                                            ))
                                            : (field.format === "YN" || field.format === "YN_BOOL" || field.format === "ValueText")
                                                ? field.options.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.text}
                                                    </option>
                                                ))
                                                /* resolveFieldOptions turns canonical loan-program
                                                   codes into this org's product names and drops the
                                                   products it does not carry. */
                                                : resolveFieldOptions(field).map((opt) => (
                                                    <option key={opt.value} value={opt.value}>{opt.text}</option>
                                                ))
                                        }

                                    </select>
                                )}

                                {field.id === 'CX.DSCR.ESTIMATED' && (
                                    <select
                                        className="form-input w-full text-sm border rounded p-1"
                                        id={`field-${field.id}`}
                                        value={localFieldValues[field.id] || ""}
                                        disabled={
                                            effectiveReadonly ||
                                            (field.id === 'CX.DSCR.ESTIMATED' && localFieldValues['CX.DSCR.LOAN.ESTIMATED'] === 'N')
                                        }
                                        onChange={(e) => {
                                            const newVal = e.target.value;
                                            removeHighlightForField(field.id);
                                            setLocalFieldValues(prev => ({ ...prev, [field.id]: newVal }));
                                            if (onFieldChange) onFieldChange(field.id, newVal);
                                        }}
                                    >
                                        <option value="">Select an Option</option>

                                        {/* --- dynamic options based on Loan Product + DSCR Type --- */}
                                        {(() => {
                                            const product = localFieldValues['CX.LOAN.PROD.BROKER.SELECT'];
                                            const docType = localFieldValues['CX.DOC.TYPE'];

                                            if (docType !== 'DSCR' || !product) return null;

                                            // Keyed by canonical loan-program code.
                                            const productDSCROptions = {
                                                // ACCELERATOR / BREEZY
                                                [LOAN_PROGRAM_CODES.PROGRAM_002]: [
                                                    { value: '1.250', text: 'DSCR ≥ 1.25' },
                                                    { value: '1.000', text: 'DSCR 1.00 - 1.24' },
                                                    { value: '0.750', text: 'DSCR 0.75 - 0.99' },
                                                    { value: '0.000', text: 'DSCR No Ratio' },
                                                ],
                                                // ACCELERATOR ITIN / BREEZY ITIN
                                                [LOAN_PROGRAM_CODES.PROGRAM_005]: [
                                                    { value: '1.000', text: 'DSCR ≥ 1.00' },
                                                    { value: '0.900', text: 'DSCR 0.90 - 0.99' },
                                                    { value: '0.800', text: 'DSCR 0.80 - 0.89' },
                                                ],
                                                // AMBASSADOR / PASSPORT
                                                [LOAN_PROGRAM_CODES.PROGRAM_007]: [
                                                    { value: '1.000', text: 'DSCR ≥ 1.00' },
                                                    { value: '0.750', text: 'DSCR 0.75 - 0.99' },
                                                    { value: '0.000', text: 'DSCR No Ratio' },
                                                ],
                                                // ACCELERATOR EQUITY FIRST / CLOUDY
                                                [LOAN_PROGRAM_CODES.PROGRAM_010]: [
                                                    { value: '1.250', text: 'DSCR ≥ 1.25' },
                                                    { value: '1.000', text: 'DSCR 1.00 - 1.24' },
                                                    { value: '0.750', text: 'DSCR 0.75 - 0.99' },
                                                    { value: '0.000', text: 'DSCR No Ratio' },
                                                ],
                                                // SKY PLUS
                                                [LOAN_PROGRAM_CODES.PROGRAM_012]: [
                                                    { value: '1.250', text: 'DSCR ≥ 1.25' },
                                                    { value: '1.000', text: 'DSCR 1.00 - 1.24' },
                                                    { value: '0.750', text: 'DSCR 0.75 - 0.99' },
                                                    { value: '0.000', text: 'DSCR No Ratio' },
                                                ]
                                            };

                                            const options = productDSCROptions[product] || [];

                                            return options.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.text}</option>
                                            ));
                                        })()}
                                    </select>
                                )}


                            </div>
                        );
                    })}
            </div>
        </div>
    );
};

export default AdditionalInformation;
